import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarClock, Loader2, Save, Plus, Trash2 } from "lucide-react";

const DAYS = [
  { v: "1", l: "Mon" },
  { v: "2", l: "Tue" },
  { v: "3", l: "Wed" },
  { v: "4", l: "Thu" },
  { v: "5", l: "Fri" },
  { v: "6", l: "Sat" },
  { v: "7", l: "Sun" },
];

export default function SchedulePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rows, setRows] = useState({});
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newShift, setNewShift] = useState({ name: "", start_time: "09:00", end_time: "17:00" });
  const [addingShift, setAddingShift] = useState(false);

  const load = useCallback(async () => {
    try {
      if (isAdmin) {
        const { data } = await api.get("/admin/schedule");
        setStaff(data.staff);
        setShifts(data.shifts);
        const init = {};
        data.staff.forEach((s) => { init[s.id] = { ...(s.assignments || {}) }; });
        setRows(init);
      } else {
        const { data } = await api.get("/schedule/my");
        setStaff([{ id: "me", name: user?.name || "Me", email: user?.email || "" }]);
        setShifts(data.shifts || []);
        setRows({ me: { ...(data.assignments || {}) } });
      }
      setDirty({});
    } catch { toast.error("Failed to load schedules"); }
    finally { setLoading(false); }
  }, [isAdmin, user]);

  useEffect(() => { load(); }, [load]);

  const setDay = (id, day, shiftId) => {
    setRows((p) => {
      const next = { ...(p[id] || {}) };
      if (shiftId) next[day] = shiftId;
      else delete next[day];
      return { ...p, [id]: next };
    });
    setDirty((p) => ({ ...p, [id]: true }));
  };

  const save = async (id) => {
    setSaving(id);
    try {
      await api.put(isAdmin ? `/admin/schedule/${id}` : "/schedule/my", { assignments: rows[id] || {} });
      toast.success("Schedule saved");
      setDirty((p) => ({ ...p, [id]: false }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(null); }
  };

  const addShift = async (e) => {
    e.preventDefault();
    if (!newShift.name.trim()) return toast.error("Shift name required");
    setAddingShift(true);
    try {
      await api.post("/admin/shifts", newShift);
      toast.success("Shift template added");
      setNewShift({ name: "", start_time: "09:00", end_time: "17:00" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAddingShift(false); }
  };

  const delShift = async (id) => {
    try {
      await api.delete(`/admin/shifts/${id}`);
      toast.success("Shift deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#1F1B16] dark-scroll text-slate-100">
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="schedule-page">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2 text-white">
            <CalendarClock className="h-6 w-6 text-amber-400" /> Work Schedule
          </h1>
          <p className="text-sm text-slate-400">{isAdmin ? "Define shift templates, then assign a shift per day for each staff. Check-ins after the shift start are flagged late." : "Set your own weekly schedule from the available shift templates. Check-ins after your shift start are flagged late."}</p>
        </div>

        {isAdmin && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid="shift-templates-card">
          <h3 className="font-display font-semibold text-lg text-white mb-3">Shift templates</h3>
          <div className="space-y-2 mb-4">
            {shifts.map((sh) => (
              <div key={sh.id} className="flex items-center justify-between rounded-lg border border-slate-800 px-3 py-2" data-testid={`shift-row-${sh.id}`}>
                <div>
                  <span className="text-sm font-medium text-white">{sh.name}</span>
                  <span className="text-xs text-slate-400 ml-2 font-mono">{sh.start_time} – {sh.end_time}</span>
                </div>
                <button onClick={() => delShift(sh.id)} className="text-slate-500 hover:text-rose-400" data-testid={`shift-delete-${sh.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {shifts.length === 0 && <p className="text-sm text-slate-500">No shift templates yet — add one below (e.g. Morning 09:00–17:00, Night 21:00–05:00).</p>}
          </div>
          <form onSubmit={addShift} className="flex flex-wrap items-center gap-2">
            <Input value={newShift.name} onChange={(e) => setNewShift({ ...newShift, name: e.target.value })} placeholder="Shift name" className="w-44 bg-slate-800 border-slate-700 text-slate-200" data-testid="shift-name-input" />
            <Input type="time" value={newShift.start_time} onChange={(e) => setNewShift({ ...newShift, start_time: e.target.value })} className="w-28 bg-slate-800 border-slate-700 text-slate-200" data-testid="shift-start-input" />
            <span className="text-slate-500 text-xs">to</span>
            <Input type="time" value={newShift.end_time} onChange={(e) => setNewShift({ ...newShift, end_time: e.target.value })} className="w-28 bg-slate-800 border-slate-700 text-slate-200" data-testid="shift-end-input" />
            <Button type="submit" size="sm" disabled={addingShift} className="bg-indigo-600 hover:bg-indigo-700" data-testid="shift-add-btn">
              {addingShift ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add shift</>}
            </Button>
          </form>
        </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
        ) : staff.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-16">No staff accounts yet.</p>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden" data-testid="staff-schedule-card">
            {staff.map((s) => {
              const r = rows[s.id] || {};
              return (
                <div key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-slate-800 last:border-0" data-testid={`schedule-row-${s.id}`}>
                  <div className="min-w-[150px]">
                    <div className="text-sm font-medium text-white">{s.name}</div>
                    <div className="text-xs text-slate-400">{s.email}</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {DAYS.map((d) => (
                      <div key={d.v} className="flex flex-col items-center gap-1">
                        <span className="text-[10px] uppercase tracking-wide text-slate-500">{d.l}</span>
                        <select
                          value={r[d.v] || ""}
                          onChange={(e) => setDay(s.id, d.v, e.target.value)}
                          className="h-8 w-[104px] rounded-lg border border-slate-700 bg-slate-800 text-xs text-slate-200 px-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                          data-testid={`schedule-shift-${s.id}-${d.v}`}
                        >
                          <option value="">Off</option>
                          {shifts.map((sh) => (
                            <option key={sh.id} value={sh.id}>{sh.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => save(s.id)}
                    disabled={!dirty[s.id] || saving === s.id}
                    className="ml-auto bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40"
                    data-testid={`schedule-save-${s.id}`}
                  >
                    {saving === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save</>}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
